import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Search, Coffee, Filter, Star, TrendingUp, AlertCircle } from 'lucide-react';
import { getApiUrl } from '../config/config';
import creatorContentService from '../services/creatorContentService';
import GoldCrown from '../components/GoldCrown';

// ==========================================
// STYLED COMPONENTS (100% Mobile Friendly)
// ==========================================
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #000000 0%, #111111 100%);
  padding: 40px 20px;
  font-family: var(--font-body, sans-serif);
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 48px;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-family: var(--font-heading, sans-serif);
  font-weight: 800;
  margin-bottom: 16px;
  
  span {
    background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const Subtitle = styled.p`
  color: #cccccc;
  font-size: clamp(1rem, 2vw, 1.2rem);
  max-width: 600px;
  margin: 0 auto 32px;
  line-height: 1.6;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 50px;
  padding: 12px 24px;
  max-width: 600px;
  margin: 0 auto;
  transition: all 0.3s ease;
  
  &:focus-within {
    border-color: #e0c16f;
    box-shadow: 0 0 0 2px rgba(224, 193, 111, 0.2);
  }

  svg { color: #666; margin-right: 12px; }
`;

const SearchInput = styled.input`
  background: transparent;
  border: none;
  color: #ffffff;
  width: 100%;
  font-size: 1rem;
  outline: none;
  
  &::placeholder { color: #666; }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 40px;
`;

const TabButton = styled.button`
  background: ${props => props.active ? 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)' : '#1a1a1a'};
  color: ${props => props.active ? '#000' : '#fff'};
  border: 1px solid ${props => props.active ? 'transparent' : '#333'};
  padding: 8px 20px;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    border-color: #e0c16f;
  }
`;

const CreatorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
`;

const CreatorCard = styled(motion.div)`
  background: #111111;
  border: 1px solid #333333;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    border-color: #555;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  }
`;

const CardBanner = styled.div`
  height: 100px;
  background: linear-gradient(135deg, #2a2a2a 0%, #111 100%);
  background-image: ${props => props.bg ? `url(${props.bg})` : 'none'};
  background-size: cover;
  background-position: center;
`;

const CardBody = styled.div`
  padding: 0 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;

const CardAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%);
  border: 4px solid #111111;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -40px;
  margin-bottom: 12px;
  overflow: hidden;
  
  img { width: 100%; height: 100%; object-fit: cover; }
  svg { width: 36px; height: 36px; color: #000; }
`;

const CardName = styled.h3`
  color: #ffffff;
  font-size: 1.2rem;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const CardBio = styled.p`
  color: #888888;
  font-size: 0.875rem;
  text-align: center;
  line-height: 1.5;
  margin-bottom: 20px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ViewProfileBtn = styled.button`
  margin-top: auto;
  width: 100%;
  padding: 10px;
  background: transparent;
  color: #e0c16f;
  border: 1px solid #e0c16f;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${CreatorCard}:hover & {
    background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%);
    color: #000;
  }
`;

const StateMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${props => props.error ? '#ff4757' : '#888888'};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  font-size: 1.1rem;

  svg {
    width: 48px;
    height: 48px;
    color: ${props => props.error ? '#ff4757' : '#555'};
  }
`;

// ==========================================
// MAIN COMPONENT
// ==========================================
const DiscoverCreators = () => {
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchCreators();
  }, []);

  const fetchCreators = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const baseUrl = getApiUrl();
      // OVO JE PROMENJENO: Gađamo tvoj pravi endpoint iz creators.js
      const response = await fetch(`${baseUrl}/creators`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch creators from server.');
      }
      
      const data = await response.json();
      // OVO JE PROMENJENO: Tvoj API vraća objekat sa nizom 'creators'
      setCreators(data.creators || []);
    } catch (err) {
      console.error('Error fetching real creators data:', err);
      setError('An error occurred while loading creators. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  // FILTERING LOGIC
  const filteredCreators = creators.filter(creator => {
    const matchesSearch = 
      (creator.display_name || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
      (creator.username || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    // U pravom sistemu, ovde možeš filtrirati i po tagovima iz baze (ako ih imaš).
    // Za sada radi po Gold statusu kao primer za tabove.
    if (activeTab === 'gold') return matchesSearch && creator.is_gold;
    return matchesSearch;
  });

  return (
    <PageContainer>
      <ContentWrapper>
        
        {/* HERO SECTION */}
        <HeroSection>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Title>Discover <span>Creators</span></Title>
            <Subtitle>
              Find your favorite creators, support their work, and unlock exclusive content directly on Trentyway.
            </Subtitle>
            
            <SearchContainer>
              <Search size={20} />
              <SearchInput 
                type="text" 
                placeholder="Search by name or username..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
          </motion.div>
        </HeroSection>

        {/* CATEGORY TABS */}
        <FilterTabs>
          <TabButton active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
            <TrendingUp size={16} /> All Creators
          </TabButton>
          <TabButton active={activeTab === 'gold'} onClick={() => setActiveTab('gold')}>
            <Star size={16} /> Gold Creators
          </TabButton>
        </FilterTabs>

        {/* CONTENT STATES (LOADING, ERROR, EMPTY, OR GRID) */}
        {loading ? (
          <StateMessage>
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
              <Coffee />
            </motion.div>
            Loading creators...
          </StateMessage>
        ) : error ? (
          <StateMessage error>
            <AlertCircle />
            {error}
          </StateMessage>
        ) : filteredCreators.length === 0 ? (
          <StateMessage>
            <Search />
            No creators found matching your criteria.
          </StateMessage>
        ) : (
          <CreatorsGrid>
            {filteredCreators.map((creator, index) => (
              <CreatorCard 
                key={creator.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => navigate(`/${creator.username}`)}
              >
                <CardBanner bg={creator.banner_image ? creatorContentService.getImageUrl(creator.banner_image) : null} />
                
                <CardBody>
                  <CardAvatar>
                    {creator.profile_image ? (
                      <img src={creatorContentService.getImageUrl(creator.profile_image)} alt="Avatar" />
                    ) : (
                      <Coffee size={32} />
                    )}
                  </CardAvatar>
                  
                  <CardName>
                    {creator.display_name || creator.username}
                    {creator.is_gold ? <GoldCrown /> : null}
                  </CardName>
                  
                  <CardBio>
                    {creator.bio || "This creator hasn't written a bio yet."}
                  </CardBio>
                  
                  <ViewProfileBtn>
                    View Profile
                  </ViewProfileBtn>
                </CardBody>
              </CreatorCard>
            ))}
          </CreatorsGrid>
        )}

      </ContentWrapper>
    </PageContainer>
  );
};

export default DiscoverCreators;